import { HeartPulse, Hospital, Route } from "lucide-react";
import { FadeUp } from "@/components/Reveal";

const STEPS = [
  {
    n: "01",
    title: "Open the app",
    body: "Sign in with Google or email in seconds. BeaconShield uses your location on your device — never stored on our servers — to find emergency rooms around you.",
    icon: HeartPulse,
  },
  {
    n: "02",
    title: "See nearby ER wait times",
    body: "Live wait estimates and distances for every ER in range, ranked from shortest to longest so you can compare at a glance.",
    icon: Hospital,
  },
  {
    n: "03",
    title: "Pick the shortest wait",
    body: "Tap the ER that gets you seen soonest and get directions. Minutes saved in a waiting room are minutes that matter.",
    icon: Route,
  },
];

export const HowItWorks = () => (
  <section id="how-it-works" data-testid="how-it-works-section" className="relative bg-transparent py-24 sm:py-32 lg:py-40">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-4">
          <FadeUp>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[var(--clinical)] flex items-center gap-3">
              <span className="h-px w-8 bg-[var(--clinical)]" /> How it works
            </p>
            <h2 className="font-display mt-5 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-[1.05]">
              Three steps.
              <br />
              Zero guesswork.
            </h2>
            <p className="mt-6 text-base text-[#b7c2d4] leading-relaxed max-w-sm">
              Built for the moment you're standing in the kitchen with a feverish kid and one question: where will we be seen fastest?
            </p>
          </FadeUp>
        </div>
        <ol className="lg:col-span-8 divide-y divide-white/10 border-t border-white/10">
          {STEPS.map((s, i) => (
            <FadeUp key={s.n} delay={i * 0.12}>
              <li
                data-testid={`how-it-works-step-${i + 1}`}
                className="group grid grid-cols-[auto_1fr] sm:grid-cols-[120px_1fr_auto] gap-x-6 sm:gap-x-10 gap-y-3 py-10 sm:py-12 transition-colors duration-500"
              >
                <span className="font-display text-5xl sm:text-6xl font-extrabold tracking-tighter text-white/10 group-hover:text-[var(--clinical)] transition-colors duration-500 leading-none">
                  {s.n}
                </span>
                <div className="sm:pt-1">
                  <h3 className="font-display text-xl sm:text-2xl font-semibold tracking-tight text-white">{s.title}</h3>
                  <p className="mt-3 text-base text-[#b7c2d4] leading-relaxed max-w-lg">{s.body}</p>
                </div>
                <span className="hidden sm:flex h-14 w-14 items-center justify-center rounded-2xl border border-white/10 text-[var(--clinical)] bg-white/5 group-hover:bg-[var(--clinical)] group-hover:text-[var(--navy)] group-hover:border-[var(--clinical)] transition-colors duration-500">
                  <s.icon size={22} strokeWidth={1.75} />
                </span>
              </li>
            </FadeUp>
          ))}
        </ol>
      </div>
    </div>
  </section>
);
